"use client";

import { useState } from 'react';
import Image from 'next/image';

interface PhotoGalleryModalProps {
    isOpen: boolean;
    onClose: () => void;
}

interface PhotoSection {
    title: string;
    photos: string[];
}

export default function PhotoGalleryModal({ isOpen, onClose }: PhotoGalleryModalProps) {
    const [selectedSection, setSelectedSection] = useState(0);

    const photoSections: PhotoSection[] = [
        {
            title: "Exterior & Views",
            photos: [
                "/cabin-front.jpg",
                "/cabin-backside-1.jpg",
                "/cabin-backside-2.jpg",
                "/cabin-backside-3.jpg",
                "/cabin-front.jpg",
                "/cabin-backside-1.jpg",
            ]
        },
        {
            title: "Living Spaces",
            photos: [
                "/cabin-front.jpg",
                "/cabin-backside-2.jpg",
                "/cabin-backside-3.jpg",
                "/cabin-front.jpg",
                "/cabin-backside-1.jpg",
            ]
        },
        {
            title: "Bedrooms",
            photos: [
                "/cabin-backside-1.jpg",
                "/cabin-backside-2.jpg",
                "/cabin-backside-3.jpg",
                "/cabin-front.jpg",
                "/cabin-backside-1.jpg",
                "/cabin-backside-2.jpg",
            ]
        },
        {
            title: "Bathrooms",
            photos: [
                "/cabin-backside-3.jpg",
                "/cabin-front.jpg",
                "/cabin-backside-1.jpg",
            ]
        },
        {
            title: "Kitchen & Dining",
            photos: [
                "/cabin-front.jpg",
                "/cabin-backside-2.jpg",
                "/cabin-backside-3.jpg",
                "/cabin-backside-1.jpg",
            ]
        },
        {
            title: "Game Room",
            photos: [
                "/cabin-backside-2.jpg",
                "/cabin-backside-3.jpg",
                "/cabin-front.jpg",
            ]
        },
        {
            title: "Outdoor Spaces",
            photos: [
                "/cabin-backside-1.jpg",
                "/cabin-backside-2.jpg",
                "/cabin-backside-3.jpg",
                "/cabin-front.jpg",
                "/cabin-backside-1.jpg",
            ]
        },
    ];

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-hidden">
            {/* Backdrop */}
            <div
                data-testid="modal-overlay"
                className="absolute inset-0 bg-black bg-opacity-90"
                onClick={onClose}
            />

            {/* Modal Content */}
            <div className="relative h-full flex flex-col">
                {/* Header */}
                <div className="relative z-10 bg-black bg-opacity-80 px-4 md:px-6 py-4 flex items-center justify-between border-b border-gray-700">
                    <h2 className="text-xl md:text-2xl font-bold">Photo Gallery</h2>
                    <button
                        onClick={onClose}
                        className="text-white hover:text-gray-300 transition-colors p-2"
                        aria-label="Close modal"
                    >
                        <svg className="w-6 h-6 md:w-8 md:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Section Tabs */}
                <div className="relative z-10 bg-black bg-opacity-80 border-b border-gray-700">
                    <div className="overflow-x-auto no-scrollbar">
                        <div className="flex gap-2 px-4 md:px-6 py-3 min-w-max">
                            {photoSections.map((section, index) => (
                                <button
                                    key={index}
                                    onClick={() => setSelectedSection(index)}
                                    className={`px-4 py-2 rounded-lg text-sm md:text-base font-medium transition-all whitespace-nowrap ${
                                        selectedSection === index
                                            ? 'bg-green-700 text-white'
                                            : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                                    }`}
                                >
                                    {section.title}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Photo Grid */}
                <div className="flex-1 overflow-y-auto p-4 md:p-6">
                    <div className="max-w-7xl mx-auto">
                        <h3 className="text-2xl md:text-3xl font-bold mb-6">
                            {photoSections[selectedSection].title}
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                            {photoSections[selectedSection].photos.map((photo, index) => (
                                <div
                                    key={index}
                                    className="relative aspect-[4/3] rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow cursor-pointer group"
                                >
                                    <Image
                                        src={photo}
                                        alt={`${photoSections[selectedSection].title} ${index + 1}`}
                                        fill
                                        className="object-cover group-hover:scale-105 transition-transform duration-300"
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Footer with photo count */}
                <div className="relative z-10 bg-black bg-opacity-80 px-4 md:px-6 py-3 border-t border-gray-700">
                    <p className="text-sm md:text-base text-gray-300 text-center">
                        {photoSections[selectedSection].photos.length} photos in {photoSections[selectedSection].title}
                    </p>
                </div>
            </div>
        </div>
    );
}
